import { watch } from "vue";
import { useModal } from "vue-final-modal";

import type {
  Controller,
  FullWorkspace,
  OrgId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import type { ModuleLogger } from "@/components/inject";
import ImportModal from "@/components/marketplace/ImportModal.vue";
import CreateOrganizationModal from "@/components/modals/CreateOrganizationModal.vue";
import OrganizationJoinedModal from "@/components/modals/OrganizationJoinedModal.vue";
import { sortOrganizationWorkspaces } from "@/components/workspaces";
import type { Menu } from "@/composables/useMenu";
import { RouteNames } from "@/routes/types";
import { browser } from "@/services/browser";
import type { Router } from "@/types";

/** Ops appended before onboarding completes (e.g. org setup wizard after self-service create). */
export const POST_ONBOARDING_QUEUED_OPS_KEY = "postOnboarding:queuedOperations";

export type PostSetupOperation =
  | { kind: "join_organization"; organizationId: OrgId }
  | { kind: "setup_organization"; organizationId: OrgId }
  | { kind: "install_extension"; extensionId: string };

export function buildPostSetupOperations(options: {
  joinOrganizationId: OrgId | null;
  extensionId: string | undefined;
}): PostSetupOperation[] {
  const ops: PostSetupOperation[] = [];
  if (options.joinOrganizationId) {
    ops.push({
      kind: "join_organization",
      organizationId: options.joinOrganizationId,
    });
  }
  if (options.extensionId) {
    ops.push({
      kind: "install_extension",
      extensionId: options.extensionId,
    });
  }
  return ops;
}

/** Append an operation to run after onboarding (merged in Routine with URL/extension-derived ops). */
export function appendPostSetupQueuedOperation(op: PostSetupOperation): void {
  const existing =
    browser.store.get<PostSetupOperation[] | null>(
      POST_ONBOARDING_QUEUED_OPS_KEY,
      null,
      { format: "json" }
    ) ?? [];
  browser.store.set(POST_ONBOARDING_QUEUED_OPS_KEY, [...existing, op], {
    format: "json",
  });
}

/**
 * Remove one queued operation (the first equal entry) from storage. Called
 * by {@link runPostSetupQueue} once the operation has been performed or has
 * failed — an operation that never settles (e.g. the app quits while it is
 * still waiting) stays queued and is retried at the next launch. No-op for
 * operations that did not come from storage (e.g. URL-derived ones).
 */
export function removePostSetupQueuedOperation(op: PostSetupOperation): void {
  const existing =
    browser.store.get<PostSetupOperation[] | null>(
      POST_ONBOARDING_QUEUED_OPS_KEY,
      null,
      { format: "json" }
    ) ?? [];
  const serialized = JSON.stringify(op);
  const index = existing.findIndex((o) => JSON.stringify(o) === serialized);
  if (index === -1) return;
  const next = existing.filter((_, i) => i !== index);
  if (next.length === 0) browser.store.unset(POST_ONBOARDING_QUEUED_OPS_KEY);
  else
    browser.store.set(POST_ONBOARDING_QUEUED_OPS_KEY, next, {
      format: "json",
    });
}

function waitForCreateOrganizationModal(organizationId: OrgId): Promise<void> {
  return new Promise((resolve) => {
    const { open } = useModal({
      component: CreateOrganizationModal,
      attrs: {
        existingOrganizationId: organizationId,
        afterClosed: () => resolve(),
      },
    });
    void open();
  });
}

/**
 * Open the welcome modal and resolve to `true` when the user clicked Open,
 * or `false` when the modal was otherwise dismissed.
 *
 * `ready` is forwarded to the modal, which renders a pending state until it
 * resolves and a success state afterwards.
 */
function waitForOrganizationJoinedModal(
  organizationId: OrgId,
  ready: Promise<void>
): Promise<boolean> {
  return new Promise((resolve) => {
    // The modal wraps `<Modal>` and emits `open` / `close` after the close
    // animation completes (in `afterClosed`), so the entry is already torn
    // down by `<ModalsContainer>` by the time we resolve here — no need to
    // call `useModal.close()` ourselves.
    const { open } = useModal({
      component: OrganizationJoinedModal,
      attrs: {
        orgId: organizationId,
        ready,
        onClose: () => resolve(false),
        onOpen: () => resolve(true),
      },
    });
    void open();
  });
}

function waitForImportModal(extensionId: string): Promise<void> {
  return new Promise((resolve) => {
    const done = () => resolve();
    const { open } = useModal({
      component: ImportModal,
      attrs: {
        extensionId,
        afterClosed: done,
        afterCancelled: done,
      },
    });
    void open();
  });
}

async function navigateToOrganizationScreen(
  router: Router,
  orgId: OrgId
): Promise<void> {
  await router.push({
    name: RouteNames.Organization,
    params: { orgId },
  });
}

type OrganizationAddedTracker = {
  /** Resolves once the org appears in `controller.organization.all()`. */
  ready: Promise<void>;
  /** Synchronous check used to decide whether to schedule a re-open. */
  isReady: () => boolean;
  /** Stop the watcher and release the controller subscription. */
  dispose: () => void;
};

/**
 * Watch the org's workspace list and pin the home of the default workspace
 * (per `sortOrganizationWorkspaces`) as soon as it has a `home` set. Both
 * the workspace list and the workspace's `home` field replicate after
 * membership lands, so this is implemented as a self-cleaning reactive
 * watcher rather than a one-shot fetch.
 */
async function pinOrganizationDefaultWorkspaceHome(
  controller: Controller,
  menu: Menu,
  logger: ModuleLogger,
  orgId: OrgId
): Promise<void> {
  type WsRef = Awaited<ReturnType<Controller["workspace"]["get"]>>;

  const workspaceIdsRef = await controller.organization.workspaces.all(orgId);
  const wsEntries = new Map<WorkspaceId, { ref: WsRef; stop: () => void }>();
  let done = false;
  let stopRoot: () => void = () => {};

  const cleanup = () => {
    stopRoot();
    for (const { ref, stop } of wsEntries.values()) {
      stop();
      ref.release();
    }
    wsEntries.clear();
    workspaceIdsRef.release();
  };

  const tryPin = (resolve: () => void) => {
    if (done) return;
    const workspaces = Array.from(
      wsEntries.values(),
      ({ ref }) => ref.value
    ).filter((w): w is FullWorkspace => w !== null);
    if (workspaces.length === 0) return;
    const home = [...workspaces].sort(sortOrganizationWorkspaces)[0]?.home;
    if (!home) return;
    done = true;
    void menu.pinObject(home, "object").catch((e) => {
      logger.error("Failed to pin organization default workspace home", e);
    });
    cleanup();
    resolve();
  };

  await new Promise<void>((resolve) => {
    stopRoot = watch(
      () => workspaceIdsRef.value,
      async (ids) => {
        if (done || !ids) return;
        const idSet = new Set(ids);
        for (const [id, entry] of [...wsEntries]) {
          if (idSet.has(id)) continue;
          entry.stop();
          entry.ref.release();
          wsEntries.delete(id);
        }
        for (const id of ids) {
          if (done || wsEntries.has(id)) continue;
          const ref = await controller.workspace.get(id);
          if (done) {
            ref.release();
            return;
          }
          const stop = watch(
            () => ref.value,
            () => tryPin(resolve)
          );
          wsEntries.set(id, { ref, stop });
        }
        tryPin(resolve);
      },
      { immediate: true }
    );
  });
}

/**
 * Watch `controller.organization.all()` for `orgId` to appear and, when it
 * does, pin the org homepage and the default workspace's home (mirroring
 * the create-organization flow). The returned tracker decouples membership
 * detection from the modal lifecycle so we can re-open the modal later if
 * the user dismissed it while pending.
 */
async function trackOrganizationAdded(
  controller: Controller,
  menu: Menu,
  logger: ModuleLogger,
  orgId: OrgId
): Promise<OrganizationAddedTracker> {
  const allRef = await controller.organization.all();
  let resolved = false;
  let resolveReady!: () => void;
  const ready = new Promise<void>((r) => {
    resolveReady = r;
  });

  const stop = watch(
    () => allRef.value,
    (ids) => {
      if (resolved || !ids?.includes(orgId)) return;
      resolved = true;
      void menu.pinOrganizationHome(orgId).catch((e) => {
        logger.error("Failed to pin organization home", e);
      });
      void pinOrganizationDefaultWorkspaceHome(
        controller,
        menu,
        logger,
        orgId
      ).catch((e) => {
        logger.error("Failed to pin org default workspace home", e);
      });
      resolveReady();
    },
    { immediate: true }
  );

  return {
    ready,
    isReady: () => resolved,
    dispose: () => {
      stop();
      allRef.release();
    },
  };
}

async function executeJoinOrganizationOperation(
  controller: Controller,
  menu: Menu,
  router: Router,
  logger: ModuleLogger,
  operation: Extract<PostSetupOperation, { kind: "join_organization" }>
): Promise<void> {
  const tracker = await trackOrganizationAdded(
    controller,
    menu,
    logger,
    operation.organizationId
  );

  const opened = await waitForOrganizationJoinedModal(
    operation.organizationId,
    tracker.ready
  );
  if (opened) {
    tracker.dispose();
    await navigateToOrganizationScreen(router, operation.organizationId);
    return;
  }
  if (tracker.isReady()) {
    // User dismissed the modal after the org was already added — nothing else
    // to do.
    tracker.dispose();
    return;
  }

  // User dismissed the pending modal. Don't block the queue; re-open the
  // welcome modal in the ready state once the backend job lands.
  void (async () => {
    try {
      await tracker.ready;
      const reopened = await waitForOrganizationJoinedModal(
        operation.organizationId,
        tracker.ready
      );
      if (reopened) {
        await navigateToOrganizationScreen(router, operation.organizationId);
      }
    } catch (e) {
      logger.error("Re-opening organization joined modal failed", e);
    } finally {
      tracker.dispose();
    }
  })();
}

async function executeSetupOrganizationOperation(
  operation: Extract<PostSetupOperation, { kind: "setup_organization" }>
): Promise<void> {
  await waitForCreateOrganizationModal(operation.organizationId);
}

async function executeInstallExtensionOperation(
  operation: Extract<PostSetupOperation, { kind: "install_extension" }>
): Promise<void> {
  await waitForImportModal(operation.extensionId);
}

/**
 * Runs setup steps sequentially after onboarding (join welcome, org wizard,
 * marketplace install).
 *
 * Each operation is removed from the queued-operations storage when it has
 * been performed or has failed; operations still pending when the app quits
 * stay in storage and run again at the next launch. A failing operation
 * does not prevent the remaining ones from running. Operations of kinds
 * queued by older versions and no longer handled (e.g. the removed
 * "set_default_meeting_automation", now covered by the launch-time
 * `setDefaultMeetingAutomation` call in Routine.vue) fall through the switch
 * and are simply removed from storage.
 */
export async function runPostSetupQueue(
  controller: Controller,
  menu: Menu,
  router: Router,
  logger: ModuleLogger,
  operations: PostSetupOperation[]
): Promise<void> {
  for (const op of operations) {
    try {
      switch (op.kind) {
        case "join_organization":
          await executeJoinOrganizationOperation(
            controller,
            menu,
            router,
            logger,
            op
          );
          break;
        case "setup_organization":
          await executeSetupOrganizationOperation(op);
          break;
        case "install_extension":
          await executeInstallExtensionOperation(op);
          break;
      }
    } catch (e) {
      logger.error("post-setup operation failed", { op, error: e });
    } finally {
      removePostSetupQueuedOperation(op);
    }
  }
}
